// src/pages/HomePage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  Compass,
  LayoutDashboard,
  Lock,
  Sparkles,
  Target,
  X,
} from "lucide-react";
import HeroSection from "../components/home/HeroSection";
import ProblemSection from "../components/home/ProblemSection";
import { SolutionSection } from "../components/home/SolutionSection";
import BridgeSection from "../components/home/Bridge";
import HowItWorksSection from "../components/home/HowItWorks";
import OutcomesSection from "../components/home/OutcomesSection";
import CTASection from "../components/home/FinalCTASection";
import FAQSection from "../components/home/FaqSection";
import FounderSection from "../components/home/FounderSection";

import AuthModal from "../components/auth/AuthModal";
import StartJourneyModal from "../components/onboarding/StartJourneyModal";
import MotivationModal from "../components/MotivationModal";

import { useEarlyAccess } from "../context/EarlyAccessContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "../firebase";
import {
  flattenProductRoadmapDays,
  getProductRoadmapCurrentDay,
  withProductRoadmapDates,
} from "../utils/buildProductRoadmap";

const PageShell = styled.div`
  position: relative;
  overflow: hidden;
`;

const ProgressTrack = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 3px;
  z-index: 1400;
  background: rgba(15, 23, 42, 0.18);
  pointer-events: none;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  width: 100%;
  transform-origin: 0% 50%;
  background: linear-gradient(90deg, #f59e0b 0%, #38bdf8 100%);
  box-shadow: 0 0 20px rgba(56, 189, 248, 0.35);
`;

const AmbientOrb = styled(motion.div)`
  position: fixed;
  border-radius: 999px;
  filter: blur(14px);
  pointer-events: none;
  z-index: 0;
  opacity: 0.55;
`;

const SectionWrap = styled(motion.section)`
  position: relative;
  z-index: 1;
`;

const fadeUp = {
  hidden: { opacity: 0, y: 34 },
  show: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.75,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const getCurrentAbsoluteDay = (startedAt) => {
  if (!startedAt) return 1;

  const start = startedAt.toDate ? startedAt.toDate() : new Date(startedAt);

  start.setHours(0, 0, 0, 0);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const diff = Math.floor((today - start) / (1000 * 60 * 60 * 24)) + 1;

  return Math.max(diff, 1);
};

const getDateKey = (date) => {
  const local = new Date(date);
  local.setHours(0, 0, 0, 0);
  return local.toISOString().slice(0, 10);
};

const getFlatRoadmapDays = (roadmap) => {
  const phases = roadmap?.roadmapRef?.phases || [];
  const startedAt = roadmap?.startedAt
    ? roadmap.startedAt.toDate
      ? roadmap.startedAt.toDate()
      : new Date(roadmap.startedAt)
    : null;
  const completedDays = roadmap?.completedDays || {};
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  let absoluteIndex = 0;

  return phases.flatMap((phase, phaseIndex) =>
    (phase.weeks || []).flatMap((week, weekIndex) =>
      (week.days || []).map((day, dayIndex) => {
        const itemDate = startedAt
          ? new Date(
              startedAt.getFullYear(),
              startedAt.getMonth(),
              startedAt.getDate() + absoluteIndex
            )
          : null;
        const dateKey = itemDate ? getDateKey(itemDate) : null;
        let status = "upcoming";

        if (dateKey && completedDays[dateKey]) {
          status = "completed";
        } else if (itemDate) {
          const compareDate = new Date(itemDate);
          compareDate.setHours(0, 0, 0, 0);
          if (compareDate.getTime() === today.getTime()) status = "today";
          else if (compareDate < today) status = "missed";
        }

        const item = {
          ...day,
          absoluteIndex,
          date: itemDate,
          dateKey,
          status,
          phaseTitle: phase.title,
          phaseIndex,
          weekTitle: week.title,
          weekNumber: week.week || weekIndex + 1,
          dayNumber: day.day || dayIndex + 1,
        };

        absoluteIndex += 1;
        return item;
      })
    )
  );
};

export default function HomePage() {
  const { open, setOpen } = useEarlyAccess();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [showMotivation, setShowMotivation] = useState(false);
  const [showReplaceRoadmapModal, setShowReplaceRoadmapModal] = useState(false);
  const [showProductBuilderModal, setShowProductBuilderModal] = useState(false);
  const [roadmap, setRoadmap] = useState(null);
  const [productRoadmap, setProductRoadmap] = useState(null);
  const [isPro, setIsPro] = useState(false);
  const [homeDecisionLoading, setHomeDecisionLoading] = useState(false);
  const { scrollYProgress } = useScroll();
  const progressScale = useSpring(scrollYProgress, {
    stiffness: 120,
    damping: 24,
    mass: 0.18,
  });
  const orbOneY = useTransform(scrollYProgress, [0, 1], [0, -180]);
  const orbTwoY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const orbThreeY = useTransform(scrollYProgress, [0, 1], [0, -120]);

  const todayKey = new Date().toISOString().slice(0, 10);

  const openProductRoadmapFlow = () => {
    if (!user) {
      setOpen(true);
      return;
    }

    navigate(productRoadmap ? "/my-product-roadmap" : "/create-product-roadmap");
  };

  /* ===============================
     1️⃣ LOAD ROADMAP FROM FIRESTORE
  =============================== */
  useEffect(() => {
    if (!user?.uid) {
      setRoadmap(null);
      setProductRoadmap(null);
      setIsPro(false);
      setHomeDecisionLoading(false);
      return;
    }

    async function loadRoadmap() {
      try {
        setHomeDecisionLoading(true);
        const userSnap = await getDoc(doc(db, "users", user.uid));
        setIsPro(userSnap.exists() && userSnap.data()?.plan === "PRO");

        const snap = await getDoc(doc(db, "roadmaps", user.uid));
        if (snap.exists()) {
          setRoadmap(snap.data());
        } else {
          setRoadmap(null);
        }

        const productSnap = await getDoc(doc(db, "product_roadmaps", user.uid));
        if (productSnap.exists()) {
          setProductRoadmap(productSnap.data());
        } else {
          setProductRoadmap(null);
        }
      } catch (err) {
        console.error("Failed to load roadmap", err);
      } finally {
        setHomeDecisionLoading(false);
      }
    }

    loadRoadmap();
  }, [user?.uid]);

  /* ===============================
     2️⃣ MOTIVATION MODAL LOGIC
  =============================== */
  useEffect(() => {
    if (!roadmap || !user?.uid) return;
    if (!roadmap?.startedAt) return;

    const lastShown = roadmap?.motivation?.lastShownDate;

    if (lastShown !== todayKey) {
      const timer = setTimeout(() => {
        setShowMotivation(true);
      }, 2500);

      return () => clearTimeout(timer);
    }
  }, [roadmap, user?.uid]);
  const currentDay = useMemo(() => {
    if (!roadmap?.startedAt) return 1;
    return getCurrentAbsoluteDay(roadmap.startedAt);
  }, [roadmap?.startedAt]);

  const flatDays = useMemo(() => getFlatRoadmapDays(roadmap), [roadmap]);
  const productFlatDays = useMemo(
    () => flattenProductRoadmapDays(productRoadmap?.roadmapRef),
    [productRoadmap?.roadmapRef]
  );

  const roadmapHomeData = useMemo(() => {
    if (!roadmap) return null;

    const completedCount = Object.keys(roadmap.completedDays || {}).length;
    const totalDays = flatDays.length;
    const percent = totalDays ? Math.round((completedCount / totalDays) * 100) : 0;
    const currentIndex = roadmap.startedAt
      ? Math.min(Math.max(currentDay - 1, 0), Math.max(flatDays.length - 1, 0))
      : 0;
    const todayItem = flatDays[currentIndex] || null;
    const currentWeekDays = todayItem
      ? flatDays.filter(
          (item) =>
            item.phaseIndex === todayItem.phaseIndex &&
            item.weekNumber === todayItem.weekNumber
        )
      : flatDays.slice(0, 7);
    const weekCompleted = currentWeekDays.filter(
      (item) => item.status === "completed"
    ).length;
    const weekMissed = currentWeekDays.filter(
      (item) => item.status === "missed"
    ).length;
    const weekUpcoming = currentWeekDays.filter(
      (item) => item.status === "upcoming"
    ).length;
    const weekToday = currentWeekDays.find((item) => item.status === "today") || null;
    const missedCount = flatDays.filter((item) => item.status === "missed").length;
    const completedKeys = roadmap.completedDays || {};
    let streak = 0;
    const streakDate = new Date();
    streakDate.setHours(0, 0, 0, 0);
    while (completedKeys[getDateKey(streakDate)]) {
      streak += 1;
      streakDate.setDate(streakDate.getDate() - 1);
    }

    return {
      totalDays,
      completedCount,
      percent,
      streak,
      todayItem,
      currentWeekDays,
      currentWeekSummary: {
        total: currentWeekDays.length,
        completed: weekCompleted,
        missed: weekMissed,
        upcoming: weekUpcoming,
        today: weekToday,
        weekTitle: todayItem?.weekTitle || "Current week",
        weekNumber: todayItem?.weekNumber || 1,
      },
      missedCount,
      started: Boolean(roadmap.startedAt),
      nextMilestone:
        todayItem?.weekTitle ||
        flatDays.find((item) => item.status !== "completed")?.title ||
        "Start your roadmap journey",
    };
  }, [roadmap, flatDays, currentDay]);

  const productHomeData = useMemo(() => {
    if (!productRoadmap) return null;

    const datedDays = withProductRoadmapDates(
      productFlatDays,
      productRoadmap?.consistency?.startedAt,
      productRoadmap?.progress?.completedDays || {}
    );

    const completedCount = Object.keys(
      productRoadmap?.progress?.completedDays || {}
    ).length;
    const totalDays = datedDays.length;
    const percent = totalDays ? Math.round((completedCount / totalDays) * 100) : 0;
    const currentIndex = productRoadmap?.consistency?.startedAt
      ? Math.min(
          Math.max(
            getProductRoadmapCurrentDay(productRoadmap.consistency.startedAt) - 1,
            0
          ),
          Math.max(datedDays.length - 1, 0)
        )
      : 0;
    const todayItem = datedDays[currentIndex] || null;
    const currentWeekDays = todayItem
      ? datedDays.filter(
          (item) =>
            item.phaseIndex === todayItem.phaseIndex &&
            item.weekNumber === todayItem.weekNumber
        )
      : datedDays.slice(0, 7);

    return {
      totalDays,
      completedCount,
      percent,
      todayItem,
      started: Boolean(productRoadmap?.consistency?.startedAt),
      currentWeekSummary: {
        total: currentWeekDays.length,
        completed: currentWeekDays.filter((item) => item.status === "completed")
          .length,
        missed: currentWeekDays.filter((item) => item.status === "missed").length,
        upcoming: currentWeekDays.filter((item) => item.status === "upcoming")
          .length,
        weekTitle: todayItem?.weekTitle || "Current build week",
        weekNumber: todayItem?.weekNumber || 1,
      },
      nextMilestone:
        todayItem?.weekFeatureFocus ||
        datedDays.find((item) => item.status !== "completed")?.title ||
        "Start your product build",
    };
  }, [productRoadmap, productFlatDays]);

  const hasLearningRoadmap = Boolean(roadmap);
  const hasProductRoadmap = Boolean(productRoadmap);
  const hasAnyRoadmap = hasLearningRoadmap || hasProductRoadmap;
  /* ===============================
     3️⃣ MARK MOTIVATION AS SHOWN
  =============================== */
  const markMotivationShown = async () => {
    if (!user?.uid) return;

    try {
      await updateDoc(doc(db, "roadmaps", user.uid), {
        "motivation.lastShownDate": todayKey,
      });
    } catch (e) {
      console.error("Failed to update motivation date");
    }
  };

  /* ===============================
     UI
  =============================== */
  return (
    <>
      <PageShell>
        <ProgressTrack>
          <ProgressFill style={{ scaleX: progressScale }} />
        </ProgressTrack>

        <AmbientOrb
          style={{
            top: "6vh",
            left: "-6vw",
            width: "26vw",
            height: "26vw",
            minWidth: 220,
            minHeight: 220,
            background:
              "radial-gradient(circle, rgba(245,158,11,0.18) 0%, rgba(245,158,11,0) 70%)",
            y: orbOneY,
          }}
          animate={{
            x: [0, 16, -10, 0],
            scale: [1, 1.08, 0.98, 1],
          }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.5 }}
          viewport={{ once: true }}
          aria-hidden
        />
        <AmbientOrb
          style={{
            top: "18vh",
            right: "-8vw",
            width: "30vw",
            height: "30vw",
            minWidth: 260,
            minHeight: 260,
            background:
              "radial-gradient(circle, rgba(56,189,248,0.18) 0%, rgba(56,189,248,0) 70%)",
            y: orbTwoY,
          }}
          animate={{
            x: [0, -20, 10, 0],
            scale: [1, 1.12, 1, 1],
          }}
          transition={{ duration: 20, repeat: Infinity, ease: "easeInOut" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.4 }}
          viewport={{ once: true }}
          aria-hidden
        />
        <AmbientOrb
          style={{
            bottom: "8vh",
            left: "-10vw",
            width: "34vw",
            height: "34vw",
            minWidth: 280,
            minHeight: 280,
            background:
              "radial-gradient(circle, rgba(245,158,11,0.14) 0%, rgba(245,158,11,0) 72%)",
            y: orbThreeY,
          }}
          animate={{
            x: [0, 24, -14, 0],
            scale: [1, 1.06, 0.96, 1],
          }}
          transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 0.34 }}
          viewport={{ once: true }}
          aria-hidden
        />

        {homeDecisionLoading ? (
          <HomeLoaderShell>
            <LoaderHero>
              <LoaderHeroMain>
                <LoaderEyebrow />
                <LoaderTitle />
                <LoaderLine $width="82%" />
                <LoaderLine $width="72%" />
                <LoaderActionRow>
                  <LoaderButton />
                  <LoaderButton $secondary />
                </LoaderActionRow>
              </LoaderHeroMain>

              <LoaderSide>
                <LoaderMetricCard>
                  <LoaderSmallLine $width="44%" />
                  <LoaderMediumLine $width="70%" />
                </LoaderMetricCard>
                <LoaderMetricCard>
                  <LoaderSmallLine $width="38%" />
                  <LoaderMediumLine $width="62%" />
                </LoaderMetricCard>
                <LoaderMetricCard>
                  <LoaderSmallLine $width="42%" />
                  <LoaderMediumLine $width="78%" />
                </LoaderMetricCard>
              </LoaderSide>
            </LoaderHero>

            <LoaderSection>
              <LoaderSectionHead>
                <LoaderSmallLine $width="18%" />
                <LoaderMediumLine $width="28%" />
              </LoaderSectionHead>
              <LoaderShortcutGrid>
                {Array.from({ length: 4 }).map((_, index) => (
                  <LoaderCard key={index}>
                    <LoaderIcon />
                    <LoaderMediumLine $width="64%" />
                    <LoaderLine $width="92%" />
                    <LoaderLine $width="76%" />
                    <LoaderTinyAction />
                  </LoaderCard>
                ))}
              </LoaderShortcutGrid>
            </LoaderSection>

            <LoaderSection>
              <LoaderSectionHead>
                <LoaderSmallLine $width="16%" />
                <LoaderMediumLine $width="24%" />
              </LoaderSectionHead>
              <LoaderFeatureGrid>
                <LoaderWideCard>
                  <LoaderSmallLine $width="28%" />
                  <LoaderLargeLine $width="56%" />
                  <LoaderLine $width="96%" />
                  <LoaderLine $width="82%" />
                </LoaderWideCard>
                <LoaderWideCard>
                  <LoaderSmallLine $width="24%" />
                  <LoaderLargeLine $width="44%" />
                  <LoaderLine $width="92%" />
                  <LoaderLine $width="74%" />
                </LoaderWideCard>
              </LoaderFeatureGrid>
            </LoaderSection>

            <LoaderSection>
              <LoaderSectionHead>
                <LoaderSmallLine $width="14%" />
                <LoaderMediumLine $width="22%" />
              </LoaderSectionHead>
              <LoaderStatsGrid>
                {Array.from({ length: 3 }).map((_, index) => (
                  <LoaderStatCard key={index}>
                    <LoaderSmallLine $width="36%" />
                    <LoaderLargeLine $width="48%" />
                    <LoaderLine $width="88%" />
                  </LoaderStatCard>
                ))}
              </LoaderStatsGrid>
            </LoaderSection>
          </HomeLoaderShell>
        ) : hasAnyRoadmap ? (
          <ExecutionHome
            as={motion.section}
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
          >
            <ExecutionHero>
              <ExecutionHeroMain>
                <ExecutionEyebrow>
                  <Sparkles size={14} />
                  Your consistency home
                </ExecutionEyebrow>
                <ExecutionTitle>
                  {hasLearningRoadmap && hasProductRoadmap
                    ? "Learning and product execution, one home"
                    : hasProductRoadmap
                    ? productHomeData?.started
                      ? "Stay in build mode today"
                      : "Your product roadmap is ready to begin"
                    : roadmapHomeData?.started
                    ? "Stay in execution mode today"
                    : "Your roadmap is ready to begin"}
                </ExecutionTitle>
                <ExecutionText>
                  {hasLearningRoadmap && hasProductRoadmap
                    ? `Your learning focus is ${
                        roadmapHomeData?.todayItem?.title ||
                        roadmapHomeData?.todayItem?.topic ||
                        "ready to review"
                      }, and your product build focus is ${
                        productHomeData?.todayItem?.title ||
                        productHomeData?.todayItem?.topic ||
                        "ready to plan"
                      }.`
                    : hasProductRoadmap
                    ? productHomeData?.started
                      ? `You are currently building ${
                          productHomeData?.todayItem?.weekFeatureFocus ||
                          productHomeData?.todayItem?.title ||
                          "your current feature"
                        }, and today’s build step is ${
                          productHomeData?.todayItem?.title ||
                          productHomeData?.todayItem?.topic ||
                          "ready to review"
                        }.`
                      : "Start from your product roadmap to unlock daily build execution, weekly feature flow, and guided development support."
                    : roadmapHomeData?.started
                    ? `You are currently in ${roadmapHomeData?.todayItem?.phaseTitle || "your roadmap"} and today’s focus is ${
                        roadmapHomeData?.todayItem?.title ||
                        roadmapHomeData?.todayItem?.topic ||
                        "ready to review"
                      }.`
                    : "Start from your roadmap to unlock the day-by-day execution system, weekly flow, and mentor support around your plan."}
                </ExecutionText>

                <ExecutionActions>
                  {hasLearningRoadmap ? (
                    <ExecutionPrimary onClick={() => navigate("/my-roadmap")}>
                      {roadmapHomeData?.started
                        ? "Open Learning Roadmap"
                        : "Start Learning Roadmap"}
                    </ExecutionPrimary>
                  ) : (
                    <ExecutionPrimary onClick={openProductRoadmapFlow}>
                      {productRoadmap ? "Open Product Roadmap" : "Create Product Roadmap"}
                    </ExecutionPrimary>
                  )}
                  {hasLearningRoadmap && (
                    <ExecutionSecondary onClick={() => navigate("/dashboard")}>
                      Learning Dashboard
                    </ExecutionSecondary>
                  )}
                  {hasProductRoadmap && (
                    <ExecutionSecondary onClick={() => navigate("/product-dashboard")}>
                      Product Dashboard
                    </ExecutionSecondary>
                  )}
                  <ExecutionSecondary onClick={openProductRoadmapFlow}>
                    {productRoadmap ? "Open Product Builder" : "Create Product Roadmap"}
                  </ExecutionSecondary>
                  {hasLearningRoadmap && (
                    <ExecutionSecondary onClick={() => setShowReplaceRoadmapModal(true)}>
                      Create New Learning Roadmap
                    </ExecutionSecondary>
                  )}
                </ExecutionActions>
              </ExecutionHeroMain>

              <ExecutionHeroSide>
                <MiniMetricCard>
                  <span>
                    {hasLearningRoadmap && hasProductRoadmap
                      ? "Learning focus"
                      : hasProductRoadmap
                      ? "Current feature"
                      : "Current phase"}
                  </span>
                  <strong>
                    {hasLearningRoadmap && hasProductRoadmap
                      ? roadmapHomeData?.todayItem?.title ||
                        roadmapHomeData?.todayItem?.topic ||
                        roadmap?.direction?.role ||
                        "Learning roadmap ready"
                      : hasProductRoadmap
                      ? productHomeData?.todayItem?.weekFeatureFocus ||
                        productHomeData?.todayItem?.title ||
                        "Product roadmap ready"
                      : roadmapHomeData?.todayItem?.phaseTitle ||
                        roadmap?.direction?.role ||
                        "Roadmap ready"}
                  </strong>
                </MiniMetricCard>
                <MiniMetricCard>
                  <span>
                    {hasLearningRoadmap && hasProductRoadmap
                      ? "Product build"
                      : hasProductRoadmap
                      ? "Current week"
                      : "Current week"}
                  </span>
                  <strong>
                    {hasLearningRoadmap && hasProductRoadmap
                      ? productHomeData?.todayItem?.title ||
                        productHomeData?.todayItem?.topic ||
                        "Open product roadmap"
                      : hasProductRoadmap
                      ? productHomeData?.todayItem
                        ? `Week ${productHomeData.todayItem.weekNumber}`
                        : "Starts after build begins"
                      : roadmapHomeData?.todayItem
                      ? `Week ${roadmapHomeData.todayItem.weekNumber}`
                      : "Starts after journey begins"}
                  </strong>
                </MiniMetricCard>
                <MiniMetricCard>
                  <span>
                    {hasLearningRoadmap && hasProductRoadmap
                      ? "Active systems"
                      : "Today focus"}
                  </span>
                  <strong>
                    {hasLearningRoadmap && hasProductRoadmap
                      ? "2 systems active"
                      : hasProductRoadmap
                      ? productHomeData?.todayItem?.title ||
                        productHomeData?.todayItem?.topic ||
                        "Open roadmap to begin"
                      : roadmapHomeData?.todayItem?.title ||
                        roadmapHomeData?.todayItem?.topic ||
                        "Open roadmap to begin"}
                  </strong>
                </MiniMetricCard>
              </ExecutionHeroSide>
            </ExecutionHero>

            <SectionBlock>
              <SectionHeaderRow>
                <div>
                  <SectionEyebrow>Quick actions</SectionEyebrow>
                  <SectionTitle>Consistency shortcuts</SectionTitle>
                </div>
              </SectionHeaderRow>
              <ShortcutGrid>
                {hasLearningRoadmap && (
                <ShortcutCard onClick={() => navigate("/dashboard")}>
                  <LayoutDashboard size={18} />
                  <strong>Learning Dashboard</strong>
                  <span>Check learning progress, momentum, and roadmap context.</span>
                  <CardArrow>
                    Open <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                )}
                {hasProductRoadmap && (
                <ShortcutCard onClick={() => navigate("/product-dashboard")}>
                  <LayoutDashboard size={18} />
                  <strong>Product Dashboard</strong>
                  <span>Track product build progress, current feature, and weekly execution.</span>
                  <CardArrow>
                    Open <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                )}
                {hasLearningRoadmap && (
                <ShortcutCard
                  onClick={() =>
                    navigate(
                      isPro && roadmapHomeData?.todayItem?.id
                        ? `/tasks-board/day?roadmapId=${roadmap?.roadmapMetaId || "current"}&dayId=${roadmapHomeData.todayItem.id}`
                        : isPro
                        ? "/my-roadmap"
                        : "/pricing"
                    )
                  }
                >
                  <Compass size={18} />
                  {!isPro && (
                    <ProBadge>
                      <Lock size={12} />
                      PRO
                    </ProBadge>
                  )}
                  <strong>Daily Task Board</strong>
                  <span>
                    {isPro
                      ? "Open today&apos;s execution board and move roadmap work into done."
                      : "Daily task board is part of PRO. Activate now to manage your day with a focused board."}
                  </span>
                  <CardArrow>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                )}
                {hasProductRoadmap && (
                <ShortcutCard
                  onClick={() =>
                    navigate(
                      isPro && productHomeData?.todayItem?.id
                        ? `/product-tasks-board/day?dayId=${productHomeData.todayItem.id}`
                        : isPro
                        ? "/my-product-roadmap"
                        : "/pricing"
                    )
                  }
                >
                  <Compass size={18} />
                  {!isPro && (
                    <ProBadge>
                      <Lock size={12} />
                      PRO
                    </ProBadge>
                  )}
                  <strong>Product Task Board</strong>
                  <span>
                    {isPro
                      ? "Move today’s product build checklist into done with one focused board."
                      : "Product task board is part of PRO. Activate now to manage your build execution."}
                  </span>
                  <CardArrow>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                )}
                {hasLearningRoadmap && (
                <ShortcutCard onClick={() => navigate(isPro ? "/roadmap/calendar" : "/pricing")}>
                  <Calendar size={18} />
                  {!isPro && (
                    <ProBadge>
                      <Lock size={12} />
                      PRO
                    </ProBadge>
                  )}
                  <strong>Calendar</strong>
                  <span>
                    {isPro
                      ? "Review today, missed days, and your execution rhythm."
                      : "Roadmap calendar is part of PRO. Activate now to unlock your daily timeline view."}
                  </span>
                  <CardArrow>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                )}
                {hasProductRoadmap && (
                <ShortcutCard
                  onClick={() => navigate(isPro ? "/product-roadmap/calendar" : "/pricing")}
                >
                  <Calendar size={18} />
                  {!isPro && (
                    <ProBadge>
                      <Lock size={12} />
                      PRO
                    </ProBadge>
                  )}
                  <strong>Product Calendar</strong>
                  <span>
                    {isPro
                      ? "Review your build timeline on real dates and stay aligned with the current feature."
                      : "Product calendar view is part of PRO. Activate now to unlock it."}
                  </span>
                  <CardArrow>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                )}
                {hasLearningRoadmap && (
                <ShortcutCard
                  onClick={() => navigate(isPro ? "/ai-mentor-chat" : "/pricing")}
                >
                  <Sparkles size={18} />
                  {!isPro && (
                    <ProBadge>
                      <Lock size={12} />
                      PRO
                    </ProBadge>
                  )}
                  <strong>AI Roadmap Assistant</strong>
                  <span>
                    {isPro
                      ? "Get help on what to do next or where you are stuck."
                      : "Daily roadmap assistance is part of PRO. Activate now to unlock it."}
                  </span>
                  <CardArrow>
                    {isPro ? "Open" : "Activate now"} <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                )}
                <ShortcutCard
                  onClick={openProductRoadmapFlow}
                >
                  <Sparkles size={18} />
                  <strong>AI Product Builder</strong>
                  <span>
                    Turn one product idea into a self-internship style roadmap with
                    daily build execution and a paid consistency system.
                  </span>
                  <CardArrow>
                    {productRoadmap ? "Open" : "Create"} <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
                <ShortcutCard onClick={() => navigate("/focus-without-phone")}>
                  <Target size={18} />
                  <strong>Focus Without Phone</strong>
                  <span>
                    Start a clean 25-minute focus block and protect one deep work
                    session from distraction.
                  </span>
                  <CardArrow>
                    Start focus <ArrowRight size={15} />
                  </CardArrow>
                </ShortcutCard>
              </ShortcutGrid>
            </SectionBlock>

            {hasLearningRoadmap && (
            <SectionBlock>
              <SectionHeaderRow>
                <div>
                  <SectionEyebrow>Progress</SectionEyebrow>
                  <SectionTitle>Progress snapshot</SectionTitle>
                </div>
              </SectionHeaderRow>
              <SnapshotGrid>
                <SnapshotCard>
                  <span>Completed days</span>
                  <strong>
                    {roadmapHomeData?.completedCount}/{roadmapHomeData?.totalDays}
                  </strong>
                  <p>{roadmapHomeData?.percent || 0}% of your roadmap is complete.</p>
                </SnapshotCard>
                <SnapshotCard>
                  <span>Streak</span>
                  <strong>{roadmapHomeData?.started ? roadmapHomeData?.streak : 0} day</strong>
                  <p>Consistency matters more than intensity for this roadmap.</p>
                </SnapshotCard>
                <SnapshotCard>
                  <span>Current focus</span>
                  <strong>
                    {roadmapHomeData?.todayItem
                      ? `Day ${roadmapHomeData.todayItem.absoluteIndex + 1}`
                      : "Journey not started"}
                  </strong>
                  <p>
                    {roadmapHomeData?.todayItem?.title ||
                      roadmapHomeData?.todayItem?.topic ||
                      "Start your roadmap to unlock today’s sequence."}
                  </p>
                </SnapshotCard>
                <SnapshotCard>
                  <span>Next milestone</span>
                  <strong>{roadmapHomeData?.nextMilestone}</strong>
                  <p>Keep moving until this week’s outcome becomes real progress.</p>
                </SnapshotCard>
              </SnapshotGrid>
            </SectionBlock>
            )}

            {hasProductRoadmap && (
            <SectionBlock>
              <SectionHeaderRow>
                <div>
                  <SectionEyebrow>Product progress</SectionEyebrow>
                  <SectionTitle>Build snapshot</SectionTitle>
                </div>
              </SectionHeaderRow>
              <SnapshotGrid>
                <SnapshotCard>
                  <span>Completed build days</span>
                  <strong>
                    {productHomeData?.completedCount}/{productHomeData?.totalDays}
                  </strong>
                  <p>{productHomeData?.percent || 0}% of your product roadmap is complete.</p>
                </SnapshotCard>
                <SnapshotCard>
                  <span>Current feature</span>
                  <strong>
                    {productHomeData?.todayItem?.weekFeatureFocus || "Feature ready"}
                  </strong>
                  <p>
                    {productHomeData?.todayItem?.title ||
                      productHomeData?.todayItem?.topic ||
                      "Open the roadmap to start building today."}
                  </p>
                </SnapshotCard>
                <SnapshotCard>
                  <span>Build week</span>
                  <strong>
                    {productHomeData?.todayItem
                      ? `Week ${productHomeData.todayItem.weekNumber}`
                      : "Build not started"}
                  </strong>
                  <p>{productHomeData?.currentWeekSummary?.weekTitle || "Current build week"}</p>
                </SnapshotCard>
                <SnapshotCard>
                  <span>Next milestone</span>
                  <strong>{productHomeData?.nextMilestone}</strong>
                  <p>Keep shipping until this feature milestone becomes live progress.</p>
                </SnapshotCard>
              </SnapshotGrid>
            </SectionBlock>
            )}

            <DualGrid>
              {hasLearningRoadmap && (
              <SectionBlock>
                <SectionHeaderRow>
                  <div>
                    <SectionEyebrow>This week</SectionEyebrow>
                    <SectionTitle>Current week snapshot</SectionTitle>
                  </div>
                </SectionHeaderRow>
                <WeekSnapshotCard>
                  <WeekSnapshotMeta>
                    Week {roadmapHomeData?.currentWeekSummary?.weekNumber}
                  </WeekSnapshotMeta>
                  <WeekSnapshotTitle>
                    {roadmapHomeData?.currentWeekSummary?.weekTitle}
                  </WeekSnapshotTitle>
                  <WeekSnapshotText>
                    {roadmapHomeData?.currentWeekSummary?.today
                      ? `Today is ${
                          roadmapHomeData.currentWeekSummary.today.title ||
                          roadmapHomeData.currentWeekSummary.today.topic ||
                          "your current lesson"
                        }.`
                      : "Open this week to review the current lesson and continue your roadmap flow."}
                  </WeekSnapshotText>

                  <WeekSummaryGrid>
                    <WeekSummaryStat>
                      <span>Total days</span>
                      <strong>{roadmapHomeData?.currentWeekSummary?.total || 0}</strong>
                    </WeekSummaryStat>
                    <WeekSummaryStat>
                      <span>Completed</span>
                      <strong>{roadmapHomeData?.currentWeekSummary?.completed || 0}</strong>
                    </WeekSummaryStat>
                    <WeekSummaryStat>
                      <span>Missed</span>
                      <strong>{roadmapHomeData?.currentWeekSummary?.missed || 0}</strong>
                    </WeekSummaryStat>
                    <WeekSummaryStat>
                      <span>Upcoming</span>
                      <strong>{roadmapHomeData?.currentWeekSummary?.upcoming || 0}</strong>
                    </WeekSummaryStat>
                  </WeekSummaryGrid>

                  <SupportLink onClick={() => navigate("/my-roadmap")}>
                    Open this week <ArrowRight size={15} />
                  </SupportLink>
                </WeekSnapshotCard>
              </SectionBlock>
              )}

              {hasLearningRoadmap && (
              <SectionBlock>
                <SectionHeaderRow>
                  <div>
                    <SectionEyebrow>Support</SectionEyebrow>
                    <SectionTitle>Get unstuck faster</SectionTitle>
                  </div>
                </SectionHeaderRow>
                <SupportStack>
                  <SupportCard>
                    <strong>Ask the AI Roadmap Assistant</strong>
                    <p>Use it when you need today&apos;s task explained, next-step clarity, or a blocker breakdown.</p>
                    {!isPro && (
                      <InlineProBadge>
                        <Lock size={12} />
                        PRO
                      </InlineProBadge>
                    )}
                    <SupportLink onClick={() => navigate(isPro ? "/ai-mentor-chat" : "/pricing")}>
                      {isPro ? "Open assistant" : "Activate now"} <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                  <SupportCard>
                    <strong>Review the full lesson</strong>
                    <p>Open the day page when you need the deeper concept, resources, and exact task context.</p>
                    <SupportLink
                      onClick={() =>
                        roadmapHomeData?.todayItem?.id &&
                        navigate(`/roadmap/day?dayId=${roadmapHomeData.todayItem.id}`)
                      }
                    >
                      View today&apos;s lesson <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                  <SupportCard>
                    <strong>Start a 25-minute focus block</strong>
                    <p>
                      Use the Pomodoro page when you need one distraction-free
                      session to move today&apos;s roadmap task forward.
                    </p>
                    <SupportLink onClick={() => navigate("/focus-without-phone")}>
                      Open focus timer <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                  <SupportCard>
                    <strong>Recovery mode</strong>
                    <p>
                      {roadmapHomeData?.missedCount
                        ? `${roadmapHomeData.missedCount} day missed. Re-open your roadmap and continue from the current focus instead of restarting.`
                        : "No recovery needed right now. Keep the chain alive with one focused session today."}
                    </p>
                    <SupportLink onClick={() => navigate("/my-roadmap")}>
                      Continue current week <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                </SupportStack>
              </SectionBlock>
              )}

              {hasProductRoadmap && (
              <SectionBlock>
                <SectionHeaderRow>
                  <div>
                    <SectionEyebrow>This build week</SectionEyebrow>
                    <SectionTitle>Current feature snapshot</SectionTitle>
                  </div>
                </SectionHeaderRow>
                <WeekSnapshotCard>
                  <WeekSnapshotMeta>
                    Week {productHomeData?.currentWeekSummary?.weekNumber}
                  </WeekSnapshotMeta>
                  <WeekSnapshotTitle>
                    {productHomeData?.currentWeekSummary?.weekTitle}
                  </WeekSnapshotTitle>
                  <WeekSnapshotText>
                    {productHomeData?.todayItem
                      ? `Today’s build step is ${
                          productHomeData.todayItem.title ||
                          productHomeData.todayItem.topic ||
                          "your current implementation task"
                        }.`
                      : "Open this week to review the current feature and continue your build flow."}
                  </WeekSnapshotText>

                  <WeekSummaryGrid>
                    <WeekSummaryStat>
                      <span>Total days</span>
                      <strong>{productHomeData?.currentWeekSummary?.total || 0}</strong>
                    </WeekSummaryStat>
                    <WeekSummaryStat>
                      <span>Completed</span>
                      <strong>{productHomeData?.currentWeekSummary?.completed || 0}</strong>
                    </WeekSummaryStat>
                    <WeekSummaryStat>
                      <span>Missed</span>
                      <strong>{productHomeData?.currentWeekSummary?.missed || 0}</strong>
                    </WeekSummaryStat>
                    <WeekSummaryStat>
                      <span>Upcoming</span>
                      <strong>{productHomeData?.currentWeekSummary?.upcoming || 0}</strong>
                    </WeekSummaryStat>
                  </WeekSummaryGrid>

                  <SupportLink onClick={() => navigate("/my-product-roadmap")}>
                    Open build week <ArrowRight size={15} />
                  </SupportLink>
                </WeekSnapshotCard>
              </SectionBlock>
              )}

              {hasProductRoadmap && (
              <SectionBlock>
                <SectionHeaderRow>
                  <div>
                    <SectionEyebrow>Product support</SectionEyebrow>
                    <SectionTitle>Ship without confusion</SectionTitle>
                  </div>
                </SectionHeaderRow>
                <SupportStack>
                  <SupportCard>
                    <strong>Open product roadmap</strong>
                    <p>Use the roadmap when you need the current feature sequence, exact daily task, and implementation order.</p>
                    <SupportLink onClick={() => navigate("/my-product-roadmap")}>
                      Open roadmap <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                  <SupportCard>
                    <strong>Review product dashboard</strong>
                    <p>Check current feature momentum, completed build days, and the next milestone you are shipping toward.</p>
                    <SupportLink onClick={() => navigate("/product-dashboard")}>
                      Open dashboard <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                  <SupportCard>
                    <strong>Plan one focus block</strong>
                    <p>Use one 25-minute session to finish today’s build step instead of overthinking the whole product.</p>
                    <SupportLink onClick={() => navigate("/focus-without-phone")}>
                      Open focus timer <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                  <SupportCard>
                    <strong>Continue the build</strong>
                    <p>
                      {productHomeData?.todayItem?.title
                        ? `Return to ${productHomeData.todayItem.title} and keep the self-internship system moving.`
                        : "Go back to your product roadmap and continue the next build step."}
                    </p>
                    <SupportLink onClick={() => navigate("/my-product-roadmap")}>
                      Continue build <ArrowRight size={15} />
                    </SupportLink>
                  </SupportCard>
                </SupportStack>
              </SectionBlock>
              )}
            </DualGrid>
          </ExecutionHome>
        ) : (
          <>
            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              animate="show"
              transition={{ duration: 0.7 }}
            >
              <HeroSection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              <ProductBuilderSection>
                <ProductBuilderShell>
                  <ProductBuilderCopy>
                    <ProductBuilderEyebrow>
                      <Sparkles size={14} />
                      Coming next for builders
                    </ProductBuilderEyebrow>
                    <ProductBuilderTitle>
                      AI Product Builder with a self-internship consistency system
                    </ProductBuilderTitle>
                    <ProductBuilderText>
                      skilll.dev is growing into one system for learning,
                      product development, and interview preparation. This next
                      path will start with a free product development roadmap,
                      then unlock the consistency layer that helps you ship
                      every day.
                    </ProductBuilderText>
                    <ProductBuilderActions>
                      <ProductBuilderPrimary
                        onClick={openProductRoadmapFlow}
                      >
                        {productRoadmap ? "Open this roadmap" : "Create product roadmap"}{" "}
                        <ArrowRight size={16} />
                      </ProductBuilderPrimary>
                      <ProductBuilderSecondary
                        onClick={() =>
                          navigate(roadmap ? "/my-roadmap" : "/career-clarity-home")
                        }
                      >
                        {roadmap ? "Stay with current roadmap" : "Create learning roadmap first"}
                      </ProductBuilderSecondary>
                    </ProductBuilderActions>
                  </ProductBuilderCopy>

                  <ProductBuilderGrid>
                    <ProductBuilderCard>
                      <span>Free roadmap</span>
                      <strong>Industry-style product build path</strong>
                      <p>
                        Product idea to phase, week, and day roadmap with MVP
                        scope, build milestones, and daily sequence.
                      </p>
                    </ProductBuilderCard>
                    <ProductBuilderCard>
                      <span>Paid consistency system</span>
                      <strong>Execution support that keeps shipping alive</strong>
                      <p>
                        Daily task board, email reminders, calendar sync,
                        roadmap calendar view, and AI daily development guidance.
                      </p>
                    </ProductBuilderCard>
                  </ProductBuilderGrid>
                </ProductBuilderShell>
              </ProductBuilderSection>
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.18 }}
            >
              <ProblemSection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.22 }}
            >
              <BridgeSection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.22 }}
            >
              <SolutionSection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              <HowItWorksSection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              <OutcomesSection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.18 }}
            >
              <FounderSection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.2 }}
            >
              <CTASection />
            </SectionWrap>

            <SectionWrap
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.16 }}
            >
              <FAQSection />
            </SectionWrap>
          </>
        )}
      </PageShell>

      {/* AUTH */}
      <AuthModal
        open={open}
        onClose={() => setOpen(false)}
        onSuccess={(mode) => {
          setOpen(false);
          navigate("/");
        }}
      />

      {/* ONBOARDING */}
      <StartJourneyModal
        open={showOnboardingModal}
        onClose={() => setShowOnboardingModal(false)}
        onStart={() => {
          setShowOnboardingModal(false);
          navigate("/onboarding");
        }}
      />

      {/* 🔥 MOTIVATION MODAL */}
      {showMotivation && roadmap?.startedAt && (
        <MotivationModal
          day={currentDay}
          tech={roadmap.claritySnapshot?.technology || "your roadmap"}
          onContinue={() => {
            markMotivationShown();
            setShowMotivation(false);
            navigate("/dashboard");
          }}
          onMentor={() => {
            markMotivationShown();
            setShowMotivation(false);
            // 🔥 mentor modal / page open
            navigate("/ai-mentor-chat");
            // or global mentor modal open
          }}
          onClose={() => {
            markMotivationShown();
            setShowMotivation(false);
          }}
        />
      )}

      {showReplaceRoadmapModal &&
        createPortal(
          <RoadmapWarningOverlay onClick={() => setShowReplaceRoadmapModal(false)}>
            <RoadmapWarningModal
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.24, ease: "easeOut" }}
            >
              <RoadmapWarningClose onClick={() => setShowReplaceRoadmapModal(false)}>
                <X size={18} />
              </RoadmapWarningClose>

              <RoadmapWarningBadge>
                <AlertTriangle size={14} />
                One roadmap at a time
              </RoadmapWarningBadge>

              <RoadmapWarningTitle>Create a new roadmap?</RoadmapWarningTitle>
              <RoadmapWarningText>
                This platform currently supports only one active roadmap at a time.
                If you complete your current roadmap, you become eligible for the next
                one. If you still choose to create a new roadmap now, your existing
                roadmap will be deleted and replaced.
              </RoadmapWarningText>

              <RoadmapWarningList>
                <li>Your current phase, week, and day progress will be removed.</li>
                <li>Your next roadmap will start fresh from a new direction.</li>
                <li>Use this only if you want to replace your current journey.</li>
              </RoadmapWarningList>

              <RoadmapWarningActions>
                <RoadmapWarningSecondary
                  onClick={() => setShowReplaceRoadmapModal(false)}
                >
                  Keep Current Roadmap
                </RoadmapWarningSecondary>
                <RoadmapWarningPrimary
                  onClick={() => {
                    setShowReplaceRoadmapModal(false);
                    navigate("/career-clarity-home", {
                      state: { replaceConfirmed: true },
                    });
                  }}
                >
                  Continue Anyway <ArrowRight size={15} />
                </RoadmapWarningPrimary>
              </RoadmapWarningActions>
            </RoadmapWarningModal>
          </RoadmapWarningOverlay>,
          document.body
        )}

      {showProductBuilderModal &&
        createPortal(
          <RoadmapWarningOverlay onClick={() => setShowProductBuilderModal(false)}>
            <RoadmapWarningModal
              onClick={(event) => event.stopPropagation()}
              initial={{ opacity: 0, y: 18, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.98 }}
              transition={{ duration: 0.24, ease: "easeOut" }}
            >
              <RoadmapWarningClose onClick={() => setShowProductBuilderModal(false)}>
                <X size={18} />
              </RoadmapWarningClose>

              <RoadmapWarningBadge>
                <Sparkles size={14} />
                AI Product Builder
              </RoadmapWarningBadge>

              <RoadmapWarningTitle>
                Build real products like a self internship
              </RoadmapWarningTitle>
              <RoadmapWarningText>
                This upcoming path is meant for developers who want more than a
                learning roadmap. You bring one product idea, and skilll.dev
                turns it into an industry-style build roadmap with daily
                execution and consistency support.
              </RoadmapWarningText>

              <RoadmapWarningList>
                <li>Free product development roadmap with phase, week, and day planning.</li>
                <li>Paid consistency system with daily task board, reminders, and calendar flow.</li>
                <li>AI Daily Development Assistant focused on what to build today.</li>
              </RoadmapWarningList>

              <RoadmapWarningActions>
                <RoadmapWarningSecondary
                  onClick={() => setShowProductBuilderModal(false)}
                >
                  Not now
                </RoadmapWarningSecondary>
                <RoadmapWarningPrimary
                  onClick={() => {
                    setShowProductBuilderModal(false);
                    if (!user) {
                      setOpen(true);
                      return;
                    }
                    navigate(
                      productRoadmap ? "/my-product-roadmap" : "/create-product-roadmap"
                    );
                  }}
                >
                  {productRoadmap ? "Open roadmap" : "Start free roadmap"}{" "}
                  <ArrowRight size={15} />
                </RoadmapWarningPrimary>
              </RoadmapWarningActions>
            </RoadmapWarningModal>
          </RoadmapWarningOverlay>,
          document.body
        )}
    </>
  );
}

const ExecutionHome = styled.div`
  position: relative;
  z-index: 1;
  width: min(1180px, calc(100% - 32px));
  margin: 118px auto 72px;

  @media (max-width: 640px) {
    width: calc(100% - 24px);
    margin-top: 96px;
  }
`;

const shimmer = `
  @keyframes homeLoaderShimmer {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const LoaderBlock = styled.div`
  ${shimmer}
  border-radius: 14px;
  background: linear-gradient(
    90deg,
    rgba(30, 41, 59, 0.78) 0%,
    rgba(51, 65, 85, 0.96) 50%,
    rgba(30, 41, 59, 0.78) 100%
  );
  background-size: 200% 100%;
  animation: homeLoaderShimmer 1.5s linear infinite;
`;

const HomeLoaderShell = styled.div`
  position: relative;
  z-index: 1;
  width: min(1180px, calc(100% - 32px));
  margin: 118px auto 72px;

  @media (max-width: 640px) {
    width: calc(100% - 24px);
    margin-top: 96px;
  }
`;

const ProductBuilderSection = styled.section`
  position: relative;
  z-index: 1;
  width: min(1180px, calc(100% - 32px));
  margin: 0 auto 28px;

  @media (max-width: 640px) {
    width: calc(100% - 24px);
  }
`;

const ProductBuilderShell = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.12fr) minmax(280px, 0.88fr);
  gap: 20px;
  padding: 28px;
  border-radius: 30px;
  background:
    radial-gradient(circle at top left, rgba(245, 158, 11, 0.14), transparent 42%),
    radial-gradient(circle at bottom right, rgba(56, 189, 248, 0.16), transparent 38%),
    linear-gradient(180deg, rgba(7, 15, 28, 0.96), rgba(5, 10, 22, 0.94));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 26px 80px rgba(0, 0, 0, 0.26);

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
    padding: 22px;
  }
`;

const ProductBuilderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const ProductBuilderEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  width: fit-content;
  padding: 9px 13px;
  border-radius: 999px;
  background: rgba(56, 189, 248, 0.12);
  border: 1px solid rgba(56, 189, 248, 0.22);
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ProductBuilderTitle = styled.h2`
  margin: 0;
  color: #f8fafc;
  font-size: clamp(1.9rem, 3vw, 3rem);
  line-height: 1.05;
  max-width: 13ch;
`;

const ProductBuilderText = styled.p`
  margin: 0;
  color: #cbd5e1;
  font-size: 1rem;
  line-height: 1.8;
  max-width: 64ch;
`;

const ProductBuilderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 8px;
`;

const ProductBuilderPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 16px;
  border: none;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  color: #08111f;
  font-weight: 900;
  cursor: pointer;
  box-shadow: 0 18px 40px rgba(56, 189, 248, 0.2);
`;

const ProductBuilderSecondary = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 14px 18px;
  border-radius: 16px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.42);
  color: #e2e8f0;
  font-weight: 800;
  cursor: pointer;
`;

const ProductBuilderGrid = styled.div`
  display: grid;
  gap: 14px;
`;

const ProductBuilderCard = styled.div`
  padding: 20px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.54);
  border: 1px solid rgba(148, 163, 184, 0.14);

  span {
    display: block;
    color: #7dd3fc;
    font-size: 11px;
    font-weight: 900;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 10px;
    color: #f8fafc;
    font-size: 1.05rem;
    line-height: 1.5;
  }

  p {
    margin: 10px 0 0;
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.75;
  }
`;

const LoaderHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.8fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const LoaderHeroMain = styled.div`
  padding: 30px;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.96), rgba(5, 11, 22, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
`;

const LoaderSide = styled.div`
  display: grid;
  gap: 14px;
`;

const LoaderMetricCard = styled.div`
  padding: 20px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.94), rgba(5, 11, 22, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
`;

const LoaderEyebrow = styled(LoaderBlock)`
  width: 140px;
  height: 32px;
  border-radius: 999px;
`;

const LoaderTitle = styled(LoaderBlock)`
  width: 68%;
  height: 56px;
  margin-top: 18px;
`;

const LoaderLine = styled(LoaderBlock)`
  width: ${({ $width }) => $width || "100%"};
  height: 14px;
  margin-top: 12px;
`;

const LoaderSmallLine = styled(LoaderBlock)`
  width: ${({ $width }) => $width || "40%"};
  height: 12px;
`;

const LoaderMediumLine = styled(LoaderBlock)`
  width: ${({ $width }) => $width || "60%"};
  height: 20px;
  margin-top: 10px;
`;

const LoaderLargeLine = styled(LoaderBlock)`
  width: ${({ $width }) => $width || "46%"};
  height: 34px;
  margin-top: 12px;
`;

const LoaderActionRow = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const LoaderButton = styled(LoaderBlock)`
  width: ${({ $secondary }) => ($secondary ? "148px" : "172px")};
  height: 48px;
  border-radius: 999px;
`;

const LoaderSection = styled.div`
  margin-top: 24px;
  padding: 24px;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.92), rgba(5, 11, 22, 0.9));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.18);
`;

const LoaderSectionHead = styled.div`
  margin-bottom: 18px;
`;

const LoaderShortcutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
`;

const LoaderCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const LoaderIcon = styled(LoaderBlock)`
  width: 20px;
  height: 20px;
  border-radius: 8px;
`;

const LoaderTinyAction = styled(LoaderBlock)`
  width: 78px;
  height: 12px;
  margin-top: 16px;
`;

const LoaderStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1040px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const LoaderFeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`;

const LoaderWideCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const LoaderStatCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const ExecutionHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.25fr) minmax(280px, 0.8fr);
  gap: 20px;
  margin-bottom: 24px;

  @media (max-width: 920px) {
    grid-template-columns: 1fr;
  }
`;

const ExecutionHeroMain = styled.div`
  padding: 30px;
  border-radius: 30px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.96), rgba(5, 11, 22, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.24);
`;

const ExecutionHeroSide = styled.div`
  display: grid;
  gap: 14px;
`;

const ExecutionEyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.22);
  color: #fbbf24;
  font-size: 12px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ExecutionTitle = styled.h1`
  margin: 18px 0 10px;
  font-size: clamp(2rem, 4vw, 3.8rem);
  line-height: 1.02;
  color: #f8fafc;
`;

const ExecutionText = styled.p`
  max-width: 720px;
  margin: 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const ExecutionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
`;

const ExecutionPrimary = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-weight: 900;
  color: #07111f;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
`;

const ExecutionSecondary = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(125, 211, 252, 0.24);
  cursor: pointer;
  font-weight: 800;
  color: #e2e8f0;
  background: rgba(15, 23, 42, 0.62);
`;

const RoadmapWarningOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(2, 6, 23, 0.74);
  backdrop-filter: blur(10px);

  @media (max-width: 640px) {
    padding: 16px;
    align-items: flex-end;
  }
`;

const RoadmapWarningModal = styled(motion.div)`
  position: relative;
  width: min(620px, 100%);
  padding: 28px;
  border-radius: 30px;
  background:
    linear-gradient(180deg, rgba(15, 23, 42, 0.96), rgba(2, 6, 23, 0.94)),
    linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(56, 189, 248, 0.1));
  border: 1px solid rgba(148, 163, 184, 0.16);
  box-shadow: 0 34px 100px rgba(0, 0, 0, 0.56);
  color: #e2e8f0;

  @media (max-width: 640px) {
    width: 100%;
    padding: 22px 18px;
    border-radius: 26px;
  }
`;

const RoadmapWarningClose = styled.button`
  position: absolute;
  top: 18px;
  right: 18px;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.66);
  color: #94a3b8;
  display: grid;
  place-items: center;
  cursor: pointer;
`;

const RoadmapWarningBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.12);
  border: 1px solid rgba(245, 158, 11, 0.2);
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const RoadmapWarningTitle = styled.h3`
  margin: 18px 0 10px;
  color: #f8fafc;
  font-size: clamp(1.8rem, 3vw, 2.4rem);
  line-height: 1.05;
`;

const RoadmapWarningText = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 15px;
  line-height: 1.8;
`;

const RoadmapWarningList = styled.ul`
  margin: 18px 0 0;
  padding-left: 18px;
  color: #cbd5e1;
  display: grid;
  gap: 10px;

  li {
    line-height: 1.7;
  }
`;

const RoadmapWarningActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 24px;
`;

const RoadmapWarningPrimary = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: none;
  cursor: pointer;
  font-weight: 900;
  color: #07111f;
  background: linear-gradient(135deg, #f59e0b, #38bdf8);
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const RoadmapWarningSecondary = styled.button`
  min-height: 48px;
  padding: 0 18px;
  border-radius: 999px;
  border: 1px solid rgba(148, 163, 184, 0.16);
  background: rgba(15, 23, 42, 0.58);
  color: #e2e8f0;
  font-weight: 800;
  cursor: pointer;
`;

const MiniMetricCard = styled.div`
  padding: 20px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.94), rgba(5, 11, 22, 0.92));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);

  span {
    display: block;
    margin-bottom: 8px;
    color: #7dd3fc;
    font-size: 11px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    font-weight: 800;
  }

  strong {
    color: #f8fafc;
    line-height: 1.5;
    font-size: 1rem;
  }
`;

const SectionBlock = styled.section`
  margin-top: 24px;
  padding: 24px;
  border-radius: 28px;
  background: linear-gradient(180deg, rgba(8, 16, 29, 0.92), rgba(5, 11, 22, 0.9));
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 22px 60px rgba(0, 0, 0, 0.18);
`;

const SectionHeaderRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 18px;
`;

const SectionEyebrow = styled.div`
  color: #7dd3fc;
  font-size: 11px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 800;
`;

const SectionTitle = styled.h2`
  margin: 6px 0 0;
  color: #f8fafc;
  font-size: 1.6rem;
`;

const ShortcutGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
`;

const ShortcutCard = styled.button`
  position: relative;
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  background: rgba(15, 23, 42, 0.56);
  color: #e2e8f0;
  text-align: left;
  cursor: pointer;
  display: grid;
  gap: 10px;
  transition: transform 0.2s ease, border-color 0.2s ease;

  &:hover {
    transform: translateY(-2px);
    border-color: rgba(125, 211, 252, 0.3);
  }

  strong {
    color: #f8fafc;
    font-size: 1rem;
  }

  span {
    color: #94a3b8;
    line-height: 1.65;
    font-size: 13px;
  }
`;

const CardArrow = styled.div`
  margin-top: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #7dd3fc;
  font-size: 12px;
  font-weight: 800;
`;

const ProBadge = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 9px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.14);
  border: 1px solid rgba(245, 158, 11, 0.24);
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SnapshotGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;

  @media (max-width: 1040px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SnapshotCard = styled.div`
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.14);

  span {
    display: block;
    color: #7dd3fc;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.08em;
  }

  strong {
    display: block;
    margin-top: 10px;
    color: #f8fafc;
    font-size: 1.28rem;
    line-height: 1.4;
  }

  p {
    margin: 10px 0 0;
    color: #94a3b8;
    font-size: 13px;
    line-height: 1.7;
  }
`;

const DualGrid = styled.div`
  display: grid;
  grid-template-columns: 1.05fr 0.95fr;
  gap: 24px;

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`;

const WeekSnapshotCard = styled.div`
  padding: 20px;
  border-radius: 24px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.14);
`;

const WeekSnapshotMeta = styled.div`
  color: #7dd3fc;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const WeekSnapshotTitle = styled.h3`
  margin: 10px 0 10px;
  color: #f8fafc;
  font-size: 1.2rem;
`;

const WeekSnapshotText = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 13px;
  line-height: 1.7;
`;

const WeekSummaryGrid = styled.div`
  margin-top: 18px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

const WeekSummaryStat = styled.div`
  padding: 14px;
  border-radius: 18px;
  background: rgba(2, 6, 23, 0.4);
  border: 1px solid rgba(148, 163, 184, 0.12);

  span {
    display: block;
    color: #94a3b8;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 8px;
    color: #f8fafc;
    font-size: 1.1rem;
  }
`;

const SupportStack = styled.div`
  display: grid;
  gap: 12px;
`;

const SupportCard = styled.div`
  position: relative;
  padding: 18px;
  border-radius: 22px;
  background: rgba(15, 23, 42, 0.56);
  border: 1px solid rgba(148, 163, 184, 0.14);

  strong {
    display: block;
    color: #f8fafc;
    font-size: 1rem;
  }

  p {
    margin: 10px 0 0;
    color: #94a3b8;
    line-height: 1.75;
    font-size: 13px;
  }
`;

const InlineProBadge = styled.div`
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 9px;
  border-radius: 999px;
  background: rgba(245, 158, 11, 0.14);
  border: 1px solid rgba(245, 158, 11, 0.24);
  color: #fbbf24;
  font-size: 11px;
  font-weight: 900;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`;

const SupportLink = styled.button`
  margin-top: 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: none;
  background: none;
  color: #7dd3fc;
  font-weight: 800;
  cursor: pointer;
  padding: 0;
`;
